import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronsUpDown, X, Search } from "lucide-react";
import { cn } from "../lib/utils";

/**
 * Multi-select combobox. Type to filter, click to pick multiple values,
 * Enter on an unmatched query adds it as a custom chip. Chips render
 * inline in the trigger and can be removed individually.
 *
 * Built WITHOUT Radix Popover (no portal, no z-index battles with
 * Leaflet / modal overlays / iPad Safari).
 *
 * Props:
 *   value:        string[]
 *   onChange:     (string[]) => void
 *   options:      string[]
 *   placeholder:  string
 *   testId:       string
 *   zIndex:       number — local stacking number for the open menu (default 1500)
 */
export function MultiSelectCombobox({
  value = [],
  onChange,
  options = [],
  placeholder = "Select…",
  testId,
  zIndex = 1500,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 300);
    return options.filter((o) => o.toLowerCase().includes(q)).slice(0, 300);
  }, [options, query]);

  const toggle = (val) => {
    if (!val) return;
    if (value.includes(val)) onChange(value.filter((v) => v !== val));
    else onChange([...value, val]);
  };

  const remove = (val, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    onChange(value.filter((v) => v !== val));
  };

  const addCustom = () => {
    const v = query.trim();
    if (!v) return;
    if (!value.includes(v)) onChange([...value, v]);
    setQuery("");
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "w-full min-h-9 flex items-center justify-between gap-2 rounded-md border border-zinc-800 bg-zinc-900 px-2.5 py-1.5 text-left text-sm text-zinc-100 hover:border-zinc-700 transition-colors"
        )}
      >
        <span className="flex flex-wrap items-center gap-1 flex-1 min-w-0">
          {value.length === 0 ? (
            <span className="text-zinc-500">{placeholder}</span>
          ) : (
            value.map((v) => (
              <span
                key={v}
                data-testid={`${testId}-chip-${v}`}
                className="inline-flex items-center gap-1 h-6 px-2 rounded bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 text-xs"
              >
                {v}
                <span
                  role="button"
                  onMouseDown={(e) => remove(v, e)}
                  aria-label={`Remove ${v}`}
                  className="text-emerald-300 hover:text-rose-300 cursor-pointer"
                >
                  <X className="w-3 h-3" />
                </span>
              </span>
            ))
          )}
        </span>
        <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
      </button>

      {open && (
        <div
          data-testid={`${testId}-menu`}
          className="absolute left-0 right-0 mt-1 rounded-md border border-zinc-700 bg-zinc-950 shadow-2xl ring-1 ring-black/40 overflow-hidden"
          style={{ zIndex }}
        >
          <div className="flex items-center gap-2 px-2.5 py-2 border-b border-zinc-800">
            <Search className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
            <input
              ref={inputRef}
              data-testid={`${testId}-input`}
              type="text"
              placeholder="Type to search or add…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  query.trim() &&
                  filtered.length === 0
                ) {
                  e.preventDefault();
                  addCustom();
                }
              }}
              className="w-full bg-transparent text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              query.trim() ? (
                <button
                  type="button"
                  onClick={addCustom}
                  data-testid={`${testId}-add-custom`}
                  className="w-full text-left px-3 py-2 text-xs text-emerald-300 hover:text-emerald-200 hover:bg-zinc-800/60"
                >
                  + Add &quot;{query.trim()}&quot;
                </button>
              ) : (
                <div className="px-3 py-2 text-xs text-zinc-500">
                  No options.
                </div>
              )
            ) : (
              filtered.map((opt) => {
                const selected = value.includes(opt);
                return (
                  <button
                    type="button"
                    key={opt}
                    data-testid={`${testId}-opt-${opt}`}
                    onClick={() => toggle(opt)}
                    className={cn(
                      "w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-zinc-800/60",
                      selected ? "text-emerald-200" : "text-zinc-100"
                    )}
                  >
                    <Check
                      className={cn(
                        "w-3.5 h-3.5 shrink-0",
                        selected
                          ? "opacity-100 text-emerald-300"
                          : "opacity-0"
                      )}
                    />
                    <span className="truncate">{opt}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MultiSelectCombobox;
